import { useVirtualizer } from '@tanstack/react-virtual';
import { useState, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { Card, Text, Badge, Button, HoverCard, Loader, ScrollArea } from '@mantine/core';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { prettyDurationSeconds } from '~/common/time/duration';
import { RunStatus } from '@prisma/client';
import clsx from 'clsx';
import { match } from 'ts-pattern';
import Link from 'next/link';
import TimeAgo from '~/components/time/TimeAgo';

export type HeatmapData = {
  // Row
  testName: string;
  testId: string;

  // Column
  results: (
    | {
        runId: string;
        suiteRunId: string;
        status: RunStatus;
        duration: number;
      }
    | undefined
  )[];
};

export function TestSuiteHeatMap({
  testSuiteId,
  suiteRunId,
  isActive,
}: {
  testSuiteId: string;
  suiteRunId?: string;
  // Since that fetch data is expensive, we only want to fetch it when the tab is active
  // Subsequent fetches will be cached
  isActive: boolean;
}) {
  const projectId = useProjectId();
  const router = useRouter();
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const { data: testSuiteData, isLoading: isTestSuiteLoading } = api.testSuite.get.useQuery(
    {
      projectId: projectId!,
      testSuiteId,
    },
    { enabled: isActive },
  );

  const { data: { testSuiteRuns = [] } = {}, isLoading: isTestSuiteRunsLoading } = api.testSuite.getRuns.useQuery(
    {
      testSuiteId,
      take: 60,
      skip: 0,
    },
    { enabled: isActive },
  );

  const [selectedLegendStatus, setSelectedLegendStatus] = useState<string>();

  // Memoized lookup map for suite runs to avoid repeated find() calls
  const suiteRunLookupMap = useMemo(() => {
    return Object.fromEntries(testSuiteRuns.map((run) => [run.id, run]));
  }, [testSuiteRuns]);

  const heatmapData = useMemo(() => {
    // Create lookup map for each suite run: suiteRunId -> { testId -> testRun }
    const suiteRunLookups = testSuiteRuns.map((suiteRun) => {
      const testRunLookup = Object.fromEntries(suiteRun.testRuns.map((testRun) => [testRun.testV2Id, testRun]));
      return { suiteRun, testRunLookup };
    });

    return testSuiteData?.suiteTests.map((suiteTest) => {
      return {
        testName: suiteTest.testV2.name,
        testId: suiteTest.testId,
        results: suiteRunLookups.map(({ suiteRun, testRunLookup }) => {
          const testRun = testRunLookup[suiteTest.testId];

          return testRun ?
              {
                runId: testRun.id,
                suiteRunId: suiteRun.id,
                status: testRun.status,
                duration: testRun.durationMs ?? 0,
              }
            : undefined;
        }),
      } satisfies HeatmapData;
    });
  }, [testSuiteRuns.length]);
  const rowVirtualizer = useVirtualizer({
    count: heatmapData ? heatmapData.length : 0,
    getScrollElement: () => scrollAreaRef.current,
    estimateSize: () => 26,
    overscan: 10,
  });

  if (isTestSuiteLoading || isTestSuiteRunsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!testSuiteData || !testSuiteRuns) {
    return <div>No data available</div>;
  }

  return (
    <div className="flex h-full w-full flex-1 flex-col overflow-hidden pt-4">
      {/* Main Heatmap */}
      <Card
        withBorder
        className="flex flex-1 flex-col"
      >
        <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
          {Object.values(RunStatus).map((status) => (
            <Legend
              key={status}
              status={status}
              isSelected={selectedLegendStatus === status}
              onClick={() => setSelectedLegendStatus(selectedLegendStatus === status ? undefined : status)}
            />
          ))}
        </div>

        <ScrollArea
          viewportRef={scrollAreaRef}
          offsetScrollbars
          classNames={{ scrollbar: 'z-20' }}
          className="flex-1"
        >
          <div className="relative min-w-max space-y-0">
            {/* Invisible dummy row to establish width for min-w-max */}
            {heatmapData && heatmapData.length > 0 && (
              <div className="pointer-events-none flex h-0 overflow-hidden opacity-0">
                <div className="mr-2 w-64 flex-shrink-0" />
                <div className="flex">
                  {heatmapData[0].results.map((_, index) => {
                    const isColumnHighlighted = suiteRunId === testSuiteRuns[index].id;
                    return (
                      <div
                        key={index}
                        className={clsx('p-px', {
                          'border-x-2': isColumnHighlighted,
                        })}
                      >
                        <div className="h-6 w-6" />
                      </div>
                    );
                  })}
                </div>{' '}
              </div>
            )}

            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const testIndex = virtualRow.index;
                const testData = heatmapData![testIndex];

                return (
                  <div
                    key={testData.testId}
                    className="absolute top-0 left-0 flex w-full"
                    style={{
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {/* Test name button - stays on left */}
                    <div className="sticky left-0 z-10 bg-white dark:bg-[var(--mantine-color-dark-6)]">
                      <HoverCard
                        width={200}
                        shadow="md"
                        position="right"
                        withArrow
                        openDelay={100}
                        closeDelay={100}
                      >
                        <HoverCard.Target>
                          <Button
                            variant="subtle"
                            className="mr-2 h-6 w-64 flex-shrink-0 p-1"
                            classNames={{
                              inner: 'justify-start overflow-hidden',
                            }}
                            color="dark"
                            component={Link}
                            href={{
                              pathname: router.pathname,
                              query: { ...router.query, testDrawer: testData.testId },
                            }}
                            as={`/project/${projectId}/test/${testData.testId}`}
                          >
                            <span className="truncate text-xs font-medium dark:text-[var(--mantine-color-text)]">
                              {testData.testName}
                            </span>
                          </Button>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {testData.testName}
                          </div>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    </div>

                    {/* Result cells - reversed so newest appears on right */}
                    <div className="flex">
                      {testData.results.map((result, index) => {
                        const isColumnHighlighted = suiteRunId === testSuiteRuns[index].id;

                        return result ?
                            <div
                              key={result.runId}
                              className={clsx('p-px', {
                                // Highlight the entire column if we're in the selected suiteRun
                                'border-x-2 border-blue-500 dark:border-blue-400': isColumnHighlighted,
                                'rounded-t-md border-t-2': testIndex === 0 && isColumnHighlighted,
                                'rounded-b-md border-b-2': testIndex === heatmapData!.length - 1 && isColumnHighlighted,
                                // Slight padding to non highlighted columns to account for the border height
                                'pt-[3px]': testIndex === 0 && !isColumnHighlighted,
                              })}
                            >
                              <HoverCard
                                width={320}
                                shadow="md"
                                position="top"
                                withArrow
                                openDelay={100}
                                closeDelay={100}
                              >
                                <HoverCard.Target>
                                  <Link
                                    href={{
                                      pathname: router.pathname,
                                      query: { ...router.query, testRunDrawer: result.runId },
                                    }}
                                    as={`/project/${projectId}/history/g_${testSuiteId}/run/${result.runId}`}
                                    target="_blank"
                                  >
                                    <div
                                      className={clsx(
                                        'h-6 w-6 cursor-pointer rounded-sm transition-all',
                                        // Special handling for SKIPPED: show color when selected, transparent when not
                                        result.status === 'SKIPPED' && selectedLegendStatus === 'SKIPPED' ?
                                          'border border-neutral-400'
                                        : getStatusColor(result.status),
                                        'hover:opacity-80',
                                        {
                                          'opacity-20': selectedLegendStatus && selectedLegendStatus !== result.status,
                                        },
                                      )}
                                    />
                                  </Link>
                                </HoverCard.Target>
                                <HoverCard.Dropdown>
                                  <div className="space-y-3">
                                    {/* Test name */}
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {testData.testName}
                                    </div>

                                    {/* Suite run info */}
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <div>
                                        Suite Run ID: #{suiteRunLookupMap[result.suiteRunId]?.id?.slice(0, 8) || 'N/A'}
                                      </div>
                                    </div>

                                    {/* Start time */}
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <div className="font-semibold">Start Time:</div>
                                      <div className="mt-1">
                                        <TimeAgo time={suiteRunLookupMap[result.suiteRunId]?.createdAt} />
                                      </div>
                                    </div>

                                    {/* Status and Duration */}
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <div>
                                        <span className="font-semibold">Status:</span> {result.status}
                                      </div>
                                      <div className="mt-1">
                                        <span className="font-semibold">Duration:</span>{' '}
                                        {prettyDurationSeconds(result.duration)}s
                                      </div>
                                    </div>
                                  </div>
                                </HoverCard.Dropdown>
                              </HoverCard>
                            </div>
                          : <div
                              key={`empty-${testData.testId}-${index}`}
                              className={clsx('p-px', {
                                'border-x-2 border-blue-500 dark:border-blue-400': isColumnHighlighted,
                                'rounded-t-md border-t-2': testIndex === 0 && isColumnHighlighted,
                                'rounded-b-md border-b-2': testIndex === heatmapData!.length - 1 && isColumnHighlighted,
                                'pt-[3px]': testIndex === 0 && !isColumnHighlighted,
                              })}
                            >
                              <HoverCard
                                width={200}
                                shadow="md"
                                position="top"
                                withArrow
                                openDelay={100}
                                closeDelay={100}
                              >
                                <HoverCard.Target>
                                  <div className="h-6 w-6 rounded-sm bg-transparent" />
                                </HoverCard.Target>
                                <HoverCard.Dropdown>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {testData.testName}
                                    </div>
                                    <div className="mt-2">No data available for this test run</div>
                                  </div>
                                </HoverCard.Dropdown>
                              </HoverCard>
                            </div>;
                      })}{' '}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Timeline indicator */}
            {!!heatmapData && heatmapData.length > 0 && (
              <div className="flex pt-2">
                {/* Empty space for test name column */}
                <div className="sticky left-0 z-10 mr-2 w-64 bg-gradient-to-r from-white to-transparent dark:from-[var(--mantine-color-dark-6)]" />

                {/* Timeline labels */}
                <div className="flex flex-1 items-center justify-between px-1">
                  <Text
                    size="xs"
                    className="font-medium text-gray-500 dark:text-neutral-400"
                  >
                    Latest
                  </Text>
                  <div className="flex flex-1 items-center justify-center">
                    <div className="mx-4 flex-1 border-t border-gray-300 dark:border-neutral-600" />
                  </div>
                  <Text
                    size="xs"
                    className="font-medium text-gray-500 dark:text-neutral-400"
                  >
                    Oldest
                  </Text>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
}

const getStatusColor = (status: RunStatus) => {
  return match(status)
    .with('PASSED', () => 'bg-green-400')
    .with('FLAKY', () => 'bg-green-600')
    .with('FAILED', () => 'bg-red-400')
    .with('RUNNING', () => 'bg-yellow-300')
    .with('CANCELLED', () => 'bg-gray-400')
    .with('SKIPPED', () => 'bg-gray-100 dark:bg-neutral-700')
    .with('QUEUED', () => 'bg-gray-700')
    .with('RETRYING', () => 'bg-yellow-600')
    .with('ERROR', () => 'bg-red-700')
    .exhaustive();
};

const Legend = ({ status, isSelected, onClick }: { status: RunStatus; isSelected: boolean; onClick: () => void }) => {
  return (
    <Badge
      variant={isSelected ? 'light' : 'subtle'}
      onClick={onClick}
      className="cursor-pointer"
      color="gray"
      radius="sm"
    >
      <div className="flex items-center gap-2">
        <div
          className={clsx('size-3 flex-shrink-0 rounded', getStatusColor(status), {
            'border border-neutral-400': status === 'SKIPPED', // Special handling for SKIPPED
          })}
        />
        <Text
          size="sm"
          className="capitalize"
        >
          {status.toLowerCase()}
        </Text>
      </div>
    </Badge>
  );
};
